import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../../api/client";
import { AuthWordmark } from "./AuthWordmark";
import { PasswordInput } from "./PasswordInput";
import { PasswordStrength } from "./PasswordStrength";

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  // No token in the URL → the link is malformed; send them back to /forgot.
  if (!token) {
    return (
      <div className="auth-screen">
        <div className="auth-card-wrap">
          <AuthWordmark />
          <div className="card">
            <div className="card-body auth-stack">
              <div className="auth-title">Invalid reset link</div>
              <p className="auth-sub">This password reset link is missing or malformed.</p>
              <Link to="/forgot" className="btn btn-primary btn-block">
                Request a new link
              </Link>
            </div>
          </div>
          <p className="auth-back">
            <Link to="/">← Back to home</Link>
          </p>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setError("Passwords don't match."); return; }

    setError("");
    setLoading(true);
    try {
      await api.post("/api/auth/reset", { token, password });
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "This reset link is invalid or has expired.");
      setLoading(false);
    }
  };

  return (
    <div className="auth-screen">
      <div className="auth-card-wrap">
        <AuthWordmark />

        <div className="card">
          <div className="card-body beta-wizard auth-stack">
            {done ? (
              <div className="signup-success">
                <div className="signup-success-icon">
                  <svg viewBox="0 0 24 24" fill="none">
                    <path d="M5 12.5l4.5 4.5L19 7" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <div className="auth-title">Password updated</div>
                <p className="auth-sub">Your password has been changed. You can now sign in with it.</p>
                <Link to="/login" className="btn btn-primary btn-block" style={{ marginTop: 4 }}>
                  Sign in
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="auth-stack">
                <div>
                  <div className="auth-title">Set a new password</div>
                  <p className="auth-sub" style={{ marginTop: 6 }}>Choose a new password for your account.</p>
                </div>

                {error && <div className="form-error" role="alert">{error}</div>}

                <label className="field">
                  <span className="field-label">New password</span>
                  <PasswordInput
                    value={password}
                    onChange={setPassword}
                    autoComplete="new-password"
                    required
                    autoFocus
                  />
                  <PasswordStrength password={password} />
                </label>

                <label className="field">
                  <span className="field-label">Confirm password</span>
                  <PasswordInput
                    value={confirm}
                    onChange={setConfirm}
                    autoComplete="new-password"
                    required
                  />
                </label>

                <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
                  {loading ? "Updating…" : "Update password"}
                </button>
              </form>
            )}
          </div>
        </div>

        <p className="auth-back">
          <Link to="/">← Back to home</Link>
        </p>
      </div>
    </div>
  );
}
